import React, { useState } from 'react';
import MentionsAndTags from './MentionsAndTags';

interface Comment {
  id: string;
  author: {
    name: string;
    avatar: string;
    level: number;
  };
  content: string;
  timestamp: string;
  likes: number;
  liked: boolean;
}

interface CommentSectionProps {
  postId: string;
  initialComments?: Comment[];
  onAddComment?: (content: string) => void;
}

export const CommentSection: React.FC<CommentSectionProps> = ({
  postId,
  initialComments = [],
  onAddComment
}) => {
  const [comments, setComments] = useState<Comment[]>(initialComments);
  const [newComment, setNewComment] = useState('');
  const [showAll, setShowAll] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    const comment: Comment = {
      id: Date.now().toString(),
      author: {
        name: 'Você',
        avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20person%20avatar%20portrait%20simple%20background%20friendly%20smile&width=100&height=100&seq=user-avatar-001&orientation=squarish',
        level: 12
      },
      content: newComment,
      timestamp: 'Agora',
      likes: 0,
      liked: false
    };

    setComments([comment, ...comments]);
    setNewComment('');
    onAddComment?.(newComment);
  };

  const handleLike = (commentId: string) => {
    setComments((prev) =>
      prev.map((comment) =>
        comment.id === commentId
          ? {
              ...comment,
              likes: comment.liked ? comment.likes - 1 : comment.likes + 1,
              liked: !comment.liked
            }
          : comment
      )
    );
  };

  const displayedComments = showAll ? comments : comments.slice(0, 3);

  return (
    <div className="space-y-4">
      {/* Formulário de Novo Comentário com Menções e Tags */}
      <form onSubmit={handleSubmit} className="flex gap-3">
        <img
          src="https://readdy.ai/api/search-image?query=professional%20business%20person%20avatar%20portrait%20simple%20background%20friendly%20smile&width=100&height=100&seq=user-avatar-002&orientation=squarish"
          alt="Seu avatar"
          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
        />
        <div className="flex-1">
          <MentionsAndTags
            value={newComment}
            onChange={setNewComment}
            placeholder="Escreva um comentário... Use @ para mencionar e # para hashtags"
          />
          <div className="flex justify-end mt-2">
            <button
              type="submit"
              disabled={!newComment.trim()}
              className="px-6 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-medium hover:from-teal-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap"
            >
              <i className="ri-send-plane-fill mr-2"></i>
              Comentar
            </button>
          </div>
        </div>
      </form>

      {/* Lista de Comentários */}
      {comments.length > 0 && (
        <div className="space-y-4">
          {displayedComments.map((comment) => (
            <div key={comment.id} className="flex gap-3">
              <img
                src={comment.author.avatar}
                alt={comment.author.name}
                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
              />
              <div className="flex-1">
                <div className="bg-gray-100 rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-gray-800">
                      {comment.author.name}
                    </span>
                    <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                      Nível {comment.author.level}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{comment.content}</p>
                </div>
                <div className="flex items-center gap-4 mt-2 px-2">
                  <button
                    onClick={() => handleLike(comment.id)}
                    className={`text-xs font-medium transition-colors ${
                      comment.liked
                        ? 'text-teal-600'
                        : 'text-gray-500 hover:text-teal-600'
                    }`}
                  >
                    {comment.liked ? '❤️' : '🤍'} Curtir
                    {comment.likes > 0 && ` (${comment.likes})`}
                  </button>
                  <span className="text-xs text-gray-500">{comment.timestamp}</span>
                </div>
              </div>
            </div>
          ))}

          {/* Botão Ver Mais */}
          {comments.length > 3 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="text-sm text-teal-600 hover:text-teal-700 font-medium ml-13"
            >
              {showAll
                ? 'Ver menos comentários'
                : `Ver todos os ${comments.length} comentários`}
            </button>
          )}
        </div>
      )}

      {comments.length === 0 && (
        <p className="text-center text-gray-500 text-sm py-4">
          Seja o primeiro a comentar! 💬
        </p>
      )}
    </div>
  );
};
